import { useEffect, useRef } from "react";
import { supabase } from "@/integrations/supabase/client";
import { recordDeviceInfo } from "@/lib/deviceDiagnostics";

/** Don't re-record more often than this while the app stays open. */
const THROTTLE_MS = 15 * 60 * 1000;

/**
 * Records the authenticated user's latest device/app version on launch and on
 * resume. Purely diagnostic: every failure is swallowed so it can never affect
 * login, startup or any workflow.
 */
export function useDeviceDiagnostics() {
  const lastRecordedAt = useRef(0);
  const inFlight = useRef(false);

  useEffect(() => {
    let cancelled = false;

    const capture = async (force = false) => {
      if (inFlight.current) return;
      const now = Date.now();
      if (!force && now - lastRecordedAt.current < THROTTLE_MS) return;
      inFlight.current = true;
      try {
        const { data } = await supabase.auth.getSession();
        const userId = data?.session?.user?.id;
        if (!userId || cancelled) return;

        const { data: member } = await supabase
          .from("company_members")
          .select("company_id")
          .eq("user_id", userId)
          .order("is_owner", { ascending: false })
          .limit(1)
          .maybeSingle();

        if (cancelled) return;
        lastRecordedAt.current = Date.now();
        await recordDeviceInfo(userId, member?.company_id ?? null);
      } catch {
        /* diagnostics only */
      } finally {
        inFlight.current = false;
      }
    };

    void capture(true);

    const onVisibility = () => {
      if (document.visibilityState === "visible") void capture();
    };
    document.addEventListener("visibilitychange", onVisibility);

    const { data: { subscription } } = supabase.auth.onAuthStateChange((event) => {
      if (event === "SIGNED_IN") void capture(true);
    });

    return () => {
      cancelled = true;
      document.removeEventListener("visibilitychange", onVisibility);
      subscription.unsubscribe();
    };
  }, []);
}
